import { useState } from "react";
import { Loader2, Trash2, ChevronRight, CopyX, CheckCircle2, Lock } from "lucide-react";

const fmtDShort = (iso) => (iso ? new Date(iso + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }) : "");

export const UnclockedDeptList = ({ items, canFix, busy, onRemove }) => {
  const [open, setOpen] = useState(true);
  const total = (items || []).reduce((a, o) => a + o.saved_hours, 0);
  return (
    <div className="mb-4 border border-border bg-card" data-testid="pa-unclocked">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-muted/40" data-testid="pa-unclocked-toggle">
        <div className="flex flex-wrap items-center gap-2">
          <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-90" : ""}`} />
          <CopyX className="h-4 w-4 text-red-600" />
          <span className="font-semibold">Hours saved in a department with no clock-in</span>
          <span className="text-xs text-muted-foreground">all properties · last 120 days · GMs &amp; Regionals emailed Mondays 8 AM CT</span>
        </div>
        {items === null ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : (
          <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${items.length ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400" : "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"}`} data-testid="pa-unclocked-count">
            {items.length ? `${items.length} entries · ${total.toFixed(2)} hrs` : "None found"}
          </span>
        )}
      </button>
      {open && items !== null && (
        <div className="overflow-x-auto border-t border-border">
          {items.length === 0 ? (
            <p className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> No duplicate department hours found.</p>
          ) : (
            <table className="w-full text-sm" data-testid="pa-unclocked-table">
              <thead>
                <tr className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2">Property</th>
                  <th className="px-4 py-2">Employee</th>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Saved Under</th>
                  <th className="px-4 py-2">Clocked As</th>
                  <th className="px-4 py-2 text-right">Hrs</th>
                  <th className="px-4 py-2 text-right">Day Saved / Clocked</th>
                  <th className="px-4 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => {
                  const key = `${o.property_id}|${o.employee_id}|${o.date}|${o.position}`;
                  return (
                    <tr key={key} className="border-t border-border" data-testid={`pa-unclocked-row-${o.employee_id}-${o.date}-${o.position}`}>
                      <td className="px-4 py-2 text-xs">{o.property_name}</td>
                      <td className="px-4 py-2 font-medium">
                        {o.name}
                        {o.override_reason && <div className="text-[0.65rem] font-normal text-amber-700 dark:text-amber-400" data-testid={`pa-unclocked-reason-${o.employee_id}-${o.date}`}>Reason ({o.override_by}): {o.override_reason}</div>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap">{fmtDShort(o.date)}</td>
                      <td className="px-4 py-2 font-medium text-red-600 dark:text-red-400">{o.position}</td>
                      <td className="px-4 py-2 text-muted-foreground">{o.clocked_positions.join(", ")}</td>
                      <td className="px-4 py-2 text-right font-mono">{o.saved_hours.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right font-mono text-xs">{o.day_saved_total.toFixed(2)} / {o.day_clocked_total.toFixed(2)}</td>
                      <td className="px-4 py-2 text-right">
                        {o.period_status === "submitted" ? (
                          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Pay period already run — reopen it on the Payroll Sheet to fix"><Lock className="h-3 w-3" /> Run</span>
                        ) : canFix ? (
                          <button type="button" onClick={() => onRemove(o)} disabled={!!busy} className="inline-flex items-center gap-1 rounded-sm border border-red-300 px-2 py-0.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:text-red-400" data-testid={`pa-unclocked-remove-${o.employee_id}-${o.date}-${o.position}`}>
                            {busy === key ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} Remove
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
